import { c as _c } from "react/compiler-runtime";
import { IcoPlusSmall16 } from '@onefootprint/icons';
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import type { IdDocKind } from '@onefootprint/request-types/dashboard';
import { LinkButton } from '@onefootprint/ui';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toDocumentTypesAndCountries } from '../../id-doc-kinds';
import type { FormData } from '../id-docs.types';
import ExistingCountryDocMappings from './components/existing-country-doc-mappings';
import Picker from './components/picker';
interface CountrySpecificIdDocPickerProps {
  setIsPickerActive: (isActive: boolean) => void;
}
const CountrySpecificIdDocPicker = t0 => {
  const $ = _c(48);
  const {
    setIsPickerActive
  } = t0;
  const {
    t: allT
  } = useTranslation("common");
  const {
    control,
    setValue
  } = useFormContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["country", "scope"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== control) {
    t2 = {
      control,
      name: t1
    };
    $[1] = control;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [countryDocMap, scope] = useWatch(t2);
  let t3;
  if ($[3] !== countryDocMap || $[4] !== scope) {
    t3 = toDocumentTypesAndCountries({
      scope,
      country: countryDocMap
    });
    $[3] = countryDocMap;
    $[4] = scope;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const shownCountryDocMap = t3.countrySpecific;
  let t4;
  if ($[6] !== shownCountryDocMap) {
    t4 = Object.keys(shownCountryDocMap);
    $[6] = shownCountryDocMap;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const existingCountries = t4;
  const isEmptyMap = existingCountries.length === 0;
  const [showPicker, setShowPicker] = useState(false);
  const [editingCountry, setEditingCountry] = useState();
  let t5;
  if ($[8] !== setIsPickerActive) {
    t5 = country => {
      setEditingCountry(country);
      setShowPicker(true);
      setIsPickerActive(true);
    };
    $[8] = setIsPickerActive;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleEdit = t5;
  let t6;
  if ($[10] !== setIsPickerActive) {
    t6 = () => {
      setEditingCountry(undefined);
      setShowPicker(true);
      setIsPickerActive(true);
    };
    $[10] = setIsPickerActive;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleAdd = t6;
  let t7;
  if ($[12] !== countryDocMap || $[13] !== editingCountry || $[14] !== setIsPickerActive || $[15] !== setValue) {
    t7 = (newCountry, newDocs) => {
      setShowPicker(false);
      setIsPickerActive(false);
      const newMap = editingCountry ? Object.fromEntries(Object.entries(countryDocMap).map(entry => entry[0] === editingCountry ? [newCountry, newDocs] : entry)) : {
        ...countryDocMap,
        [newCountry]: newDocs
      };
      setEditingCountry(undefined);
      setValue("country", newMap);
    };
    $[12] = countryDocMap;
    $[13] = editingCountry;
    $[14] = setIsPickerActive;
    $[15] = setValue;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const handleSave = t7;
  let t8;
  if ($[17] !== setIsPickerActive) {
    t8 = () => {
      setShowPicker(false);
      setIsPickerActive(false);
      setEditingCountry(undefined);
    };
    $[17] = setIsPickerActive;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleCancel = t8;
  let t9;
  if ($[19] !== countryDocMap || $[20] !== editingCountry || $[21] !== setIsPickerActive || $[22] !== setValue) {
    t9 = () => {
      setShowPicker(false);
      setIsPickerActive(false);
      const newMap_0 = {
        ...countryDocMap
      };
      if (editingCountry) {
        delete newMap_0[editingCountry];
      }
      setEditingCountry(undefined);
      setValue("country", newMap_0);
    };
    $[19] = countryDocMap;
    $[20] = editingCountry;
    $[21] = setIsPickerActive;
    $[22] = setValue;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleRemove = t9;
  let t10;
  if ($[24] !== countryDocMap || $[25] !== editingCountry) {
    t10 = editingCountry ? {
      [editingCountry]: countryDocMap[editingCountry]
    } : undefined;
    $[24] = countryDocMap;
    $[25] = editingCountry;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== handleCancel || $[28] !== handleRemove || $[29] !== handleSave || $[30] !== t10) {
    t11 = <Picker editingCountryDocMap={t10} onSave={handleSave} onCancel={handleCancel} onRemove={handleRemove} />;
    $[27] = handleCancel;
    $[28] = handleRemove;
    $[29] = handleSave;
    $[30] = t10;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const picker = t11;
  let t12;
  if ($[32] !== editingCountry || $[33] !== handleEdit || $[34] !== isEmptyMap || $[35] !== picker || $[36] !== shownCountryDocMap) {
    t12 = isEmptyMap ? null : <ExistingCountryDocMappings countryDocMappings={shownCountryDocMap} editingCountry={editingCountry} editor={picker} onEdit={handleEdit} />;
    $[32] = editingCountry;
    $[33] = handleEdit;
    $[34] = isEmptyMap;
    $[35] = picker;
    $[36] = shownCountryDocMap;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== allT || $[39] !== editingCountry || $[40] !== handleAdd || $[41] !== isEmptyMap || $[42] !== picker || $[43] !== showPicker) {
    t13 = showPicker && !editingCountry ? picker : <LinkButton onClick={handleAdd} iconComponent={IcoPlusSmall16} iconPosition="left">{isEmptyMap ? allT(_temp) : allT(_temp2)}</LinkButton>;
    $[38] = allT;
    $[39] = editingCountry;
    $[40] = handleAdd;
    $[41] = isEmptyMap;
    $[42] = picker;
    $[43] = showPicker;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t12 || $[46] !== t13) {
    t14 = <div className="flex flex-col gap-3">{t12}{t13}</div>;
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  return t14;
};
export default CountrySpecificIdDocPicker;
function _temp($) {
  return $.add;
}
function _temp2($_0) {
  return $_0.addMore;
}